import React, { useState } from 'react';
import Button from './buttons/Button';
import InputField from './inputFields/InputField';
import ShowModal from '../utils/ShowModal';
import { PlusIcon } from 'lucide-react';
import { Form, Formik } from 'formik';
import FormikDropdown from './inputFields/FormikDropDown';

const DetailedViewModal = ({
    show,
    onClose,
    report,
    onStatusUpdate,
    getStatusBadge,
    getSeverityBadge,
    getRepairStatusBadge
}) => {
    const [showAddNoteModal, setShowAddNoteModal] = useState(false);
    const [newNote, setNewNote] = useState({
        note: '',
        status: '',
        priority: 'medium'
    });

    const statusOptions = [
        { value: 'reported', label: 'Reported' },
        { value: 'investigating', label: 'Investigating' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'damaged', label: 'Damaged' },
        { value: 'repairable', label: 'Repairable' },
        { value: 'repaired', label: 'Repaired' }
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
    ];

    const handleAddNote = (values) => {
        if (values.note && values.status) {
            // Here you would typically save the note to your data
            console.log('Adding note:', values);
            setNewNote({ note: '', status: '', priority: 'medium' });
            setShowAddNoteModal(false);
        }
    };

    return (
        <>
            {/* Main Details Modal */}
            <ShowModal
                show={show}
                onClose={onClose}
                title={report ? `Details: ${report.itemName}` : "Item Details"}
                size="lg"
                sidebar={true}
                componentToPass={report ? <div className="space-y-6">
                    {/* Item Information Section */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex flex-row justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                                Item Information
                            </h3>
                            <Button className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-xs rounded flex items-center gap-2" onClick={() => setShowAddNoteModal(true)}>
                                <PlusIcon size={16} />
                                <span className="text-sm">
                                    Add Note
                                </span>
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Item Name</p>
                                <p className="font-medium text-gray-900">{report.itemName}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">SKU</p>
                                <p className="font-medium text-gray-900">{report.sku}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Quantity</p>
                                <p className="font-medium text-gray-900">{report.quantity} units</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Location</p>
                                <p className="font-medium text-gray-900">{report.location}</p>
                            </div>
                            {report.supplier && (
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Supplier</p>
                                    <p className="font-medium text-gray-900">{report.supplier}</p>
                                </div>
                            )}
                            {report.customer && (
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Customer</p>
                                    <p className="font-medium text-gray-900">{report.customer}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Damage Details Section */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                            Damage Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Damage Type</p>
                                <p className="font-medium text-gray-900 capitalize">{report.damageType.replace('_', ' ')}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Severity</p>
                                <span className={getSeverityBadge(report.severity)}>
                                    {report.severity.charAt(0).toUpperCase() + report.severity.slice(1)}
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Estimated Loss</p>
                                <p className="font-semibold text-red-600">{report.estimatedLoss}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Status</p>
                                <span className={getStatusBadge(report.status)}>
                                    {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                </span>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm text-gray-600 mb-2">Description</p>
                            <p className="text-gray-900 bg-gray-50 p-3 rounded border">{report.description}</p>
                        </div>
                    </div>

                    {/* Notes Section */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                            Notes
                        </h3>

                        <div className="flex flex-col gap-4">
                            {(report.notes??[]).map((note, index) => (
                                <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                                    <p className="text-sm text-gray-600 mb-1">Note {index + 1}</p>
                                    <p className="text-gray-900">{note.note}</p>
                                    <p className="text-sm text-gray-600 mb-1">Status</p>
                                    <span className={getStatusBadge(note.status)}>
                                        {note.status.charAt(0).toUpperCase() + note.status.slice(1)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div> : null}
            />
            <ShowModal
                isOpen={showAddNoteModal}
                title={"Add Note"}
                componentToPass={<div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Comment</h3>
                    <Formik
                        initialValues={{ note: "", status: "pending" }}
                        onSubmit={(values, { resetForm }) => {
                            handleAddNote(values);
                            resetForm();
                        }}
                    >
                        {({ handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <FormikDropdown
                                    label="Status"
                                    name="status"
                                    options={statusOptions}
                                />
                                <InputField
                                    label="Note"
                                    name="note"
                                    placeholder="Enter your note..."
                                    rows={4}
                                    required
                                />

                                <div className="flex gap-3 mt-4 justify-end">
                                    <Button
                                        type="submit"
                                        className="bg-primary hover:bg-green-600 flex-1 text-center text-white font-medium rounded-lg text-sm px-4 py-2.5 gap-2 transition-colors duration-200"
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => setShowAddNoteModal(false)}
                                        className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 transition-colors duration-200"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>}
                onRequestClose={() => setShowAddNoteModal(false)}
            />
        </>
    );
};

export default DetailedViewModal;